import { useMemo, useRef } from 'react';
import { themeQuartz, type GridApi, type GridReadyEvent } from 'ag-grid-enterprise';
import { useGridConfig } from './useGridConfig';
import { useGridFilters } from './useGridFilters';
import { useRowSelection } from './useRowSelection';
import type { IRow } from '../types/TableRows';

export const useReconGrid = (data?: IRow[]) => {
  const rowData: IRow[] | undefined = useMemo(() => data?.map((row) => ({ ...row })), [data]);

  const { colDefs, defaultColDef, rowSelection, getRowId, autoSizeStrategy } = useGridConfig();
  const gridApiRef = useRef<GridApi | null>(null);

  const { filters, filterLabels, handleFilterChange, applyFilters } = useGridFilters();

  const {
    selectedRow,
    comment,
    setComment,
    breakId,
    modalOpen,
    onSelectionChanged,
    onRowClicked,
    handleSaveComment,
    handleCloseModal,
  } = useRowSelection(gridApiRef);

  const getUniqueValuesFromRows = (
    rows: IRow[] | undefined,
    field: keyof IRow,
    activeFilters: Partial<Record<keyof IRow, string>>
  ): string[] => {
    const filtersExcludingCurrent = Object.entries(activeFilters).reduce<Partial<Record<keyof IRow, string>>>(
      (acc, [key, value]) => {
        if (key !== field && value) {
          acc[key as keyof IRow] = value;
        }
        return acc;
      },
      {}
    );

    const filteredRows = rows?.filter((row) =>
      Object.entries(filtersExcludingCurrent).every(([key, value]) => row[key as keyof IRow] === value)
    );

    const values = filteredRows?.map((row) => row[field]);
    const uniqueValues = Array.from(new Set(values));
    return uniqueValues.filter((v): v is string => typeof v === 'string');
  };

  const getUniqueValues = (field: keyof IRow) => getUniqueValuesFromRows(rowData, field, filters);

  const onGridReady = (params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  };

  return {
    gridApiRef,
    filters,
    applyFilters,
    filterLabels,
    handleFilterChange,
    getUniqueValues,
    colDefs,
    defaultColDef,
    rowSelection,
    onGridReady,
    onSelectionChanged,
    onRowClicked,
    getRowId,
    modalOpen,
    handleCloseModal,
    handleSaveComment,
    comment,
    setComment,
    selectedRow,
    breakId,
    autoSizeStrategy,
    theme: themeQuartz.withParams({ browserColorScheme: 'light', headerFontSize: 14 }),
  };
};
